import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Flex } from '@components/Flex';
import { Container, NavIcon, NavText } from './FooterNavs.styles';
import { CTALink } from '@components/CTALink';
import { useShopStorefrontContext } from '@utils/storefront/ShopStorefrontProvider';
import { useTranslation } from 'react-i18next';
import { JoykitIconVariant } from '@components/JoykitIcon';

type navType = { icon: JoykitIconVariant; name: string; path?: string; id: string };

export const FooterNavs = () => {
  const { config } = useShopStorefrontContext();
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [isHidden, updateStatus] = useState(false);
  const [isIos15Safari, setIsIos15Safari] = useState(false);

  // Use refs to avoid stale closure issues
  const isHiddenRef = useRef(isHidden);
  const oldScrollRef = useRef(0);
  const scrollDownStartRef = useRef<number | null>(null);
  const isInitializedRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    isHiddenRef.current = isHidden;
  }, [isHidden]);

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', (e) => {
      // Prevent the android chrome mini-infobar from appearing on mobile
      e.preventDefault();
    });
  }, []);

  useEffect(() => {
    setActiveTab(window.location.pathname);
  }, []);

  const handleScroll = useCallback(() => {
    // Grace period: don't hide during initial scroll restoration
    if (!isInitializedRef.current) {
      return;
    }

    const currentScroll = window.pageYOffset;
    const scrollDiff = oldScrollRef.current - currentScroll;

    if (scrollDiff < 0) {
      // Handle Scroll down
      if (!scrollDownStartRef.current) {
        scrollDownStartRef.current = currentScroll;
      }
      if (scrollDownStartRef.current && currentScroll - scrollDownStartRef.current > 600) {
        if (!isHiddenRef.current) {
          scrollDownStartRef.current = null;
          updateStatus(true);
        }
      }
    } else if (scrollDiff > 0) {
      // Handle Scroll up
      if (isHiddenRef.current) {
        scrollDownStartRef.current = null;
        updateStatus(false);
      }
    }
    oldScrollRef.current = currentScroll;
  }, []);

  useEffect(() => {
    // Initialize scroll position
    oldScrollRef.current = window.pageYOffset;

    // Add a grace period before enabling scroll-to-hide
    // This prevents the nav from hiding during browser scroll restoration
    const timer = setTimeout(() => {
      isInitializedRef.current = true;
    }, 500);

    window.addEventListener('scroll', handleScroll, { passive: true });
    setIsIos15Safari(!!window.navigator.userAgent.match('OS 15_') && !!window.navigator.userAgent.match('Safari'));

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
      isInitializedRef.current = false;
    };
  }, [handleScroll]);

  const { t } = useTranslation('footerNavs');
  const navs = t('navs') as navType[];

  if (!config.mobileFooterNavEnabled) {
    return null;
  }

  return (
    <Container isHidden={isHidden} isIos15Safari={isIos15Safari}>
      {navs.map((navs: navType) => {
        const isActive = activeTab === navs.path;
        return (
          <CTALink key={navs.id} id={navs.id} href={navs.path || ''} variant="footerAlt">
            <Flex flexDirection="column" alignItems="center">
              <NavIcon name={navs.icon} color="neutra5" marginBottom={[1]} isActive={isActive} />
              <NavText variant="legal" tagName="span" color="neutra5" isActive={isActive} name={navs.name}>
                {navs.name}
              </NavText>
            </Flex>
          </CTALink>
        );
      })}
    </Container>
  );
};
